import React, {useContext} from 'react';
import glamorous from 'glamorous';
import {CHARCOAL, FOCUS_BLUE} from '../../../shared/style/colors';
import {BASE_TEXT, WEIGHT, FONTS} from '../../../shared/style/typography';
import {PAGE_WIDTH} from '../../../shared/style/dimensions';
import {ALPHA} from '../../../shared/style/color-utils';
import Line from '../shared/icons/line.svg';
import {MobileContext} from '../../../shared/enhancers/mobile-enhancer';
import {LAPTOP, PHONE} from '../../../shared/style/breakpoints';
import Cta from './cta';
import {githubMarketplace} from '../constants';
import sanitizeData from './../../../shared/utils/sanitize-data';

const Container = glamorous.div({
  backgroundColor: ALPHA(FOCUS_BLUE, 0.03),
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignContent: 'center',
  padding: '0 20px'
});

const SubContainer = glamorous.div({
  maxWidth: PAGE_WIDTH,
  display: 'flex',
  flexDirection: ' column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  [PHONE]: {
    width: '85%',
    margin: '0 20px'
  }
});

const Title = glamorous.h3({
  ...BASE_TEXT,
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  lineHeight: '53px',
  color: CHARCOAL,
  fontWeight: WEIGHT.ULTRA_BLACK,
  fontSize: 44,
  textAlign: 'center',
  marginTop: 101,
  [PHONE]: {
    fontSize: 28,
    lineHeight: '30px',
    marginTop: 51
  }
});

const SubTitle = glamorous.h4({
  ...BASE_TEXT,
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  lineHeight: '44px',
  color: CHARCOAL,
  fontWeight: WEIGHT.NORMAL,
  fontSize: 24,
  textAlign: 'center',
  margin: 0,
  padding: 0,
  marginBottom: 66,
  [PHONE]: {
    fontSize: 20,
    lineHeight: '35px',
    marginBottom: 80
  }
});

const Content = glamorous.div({
  width: '100%',
  position: 'relative',
  marginTop: 66,
  display: 'flex',
  flexDirection: 'column',
  [PHONE]: {
    marginTop: 0
  }
});

const Stepper = glamorous.div({
  display: 'flex',
  flexDirection: 'column'
});

const Step = glamorous.div(
  {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridColumnGap: 50,
    marginBottom: 50,
    marginTop: -42,
    [LAPTOP]: {
      marginTop: 0
    },
    [PHONE]: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: -42
    }
  },
  ({placement}) => ({
    [LAPTOP]: {
      gridTemplateColumns: placement % 2 === 0 ? '30% 70%' : '70% 30%',
      gridColumnGap: 20
    }
  })
);

const StepText = glamorous.p(
  {
    ...BASE_TEXT,
    fontFamily: `${FONTS.CEREBRI_SANS_PRO} !important`,
    fontWeight: WEIGHT.NORMAL,
    fontSize: 22,
    lineHeight: '43px',
    color: CHARCOAL,
    margin: 0,
    padding: 0,
    marginTop: 46,
    [LAPTOP]: {
      fontSize: 21
    },
    [PHONE]: {
      marginTop: 0,
      marginBottom: 50,
      marginLeft: 0,
      fontSize: 20,
      lineHeight: '40px'
    },
    '>a': {
      fontWeight: WEIGHT.ULTRA_BOLD,
      ':hover': {
        cursor: 'pointer',
        textDecoration: 'underline'
      },
      ':focus': {
        color: FOCUS_BLUE,
        textDecoration: 'underline'
      }
    }
  },
  ({extraStyle}) => (extraStyle ? extraStyle : {})
);

const LineStyled = glamorous(Line)({
  position: 'absolute',
  textAlign: 'center',
  top: '-2%',
  left: '50%',
  zIndex: '-99',
  [LAPTOP]: {
    height: '94%'
  }
});

const StepImage = glamorous.img(
  {
    width: 650,
    height: 'auto',
    margin: '0 auto',
    [LAPTOP]: {
      width: '100%'
    },
    [PHONE]: {
      width: 350,
      height: 'auto'
    }
  },
  ({imageStyle}) => (imageStyle ? imageStyle : {})
);

const StepLink = glamorous.a({
  '&:hover': {cursor: 'pointer'}
});

const Steps = [
  {
    icon: 'https://img.stackshare.io/fe/private/stackshare-app_2x.png',
    text: `Head over to GitHub to install the <a href=${githubMarketplace} target="_blank"
    rel="noreferrer noopener nofollow"
    title="Github Marketplace Link">StackShare App</a>, click <strong>"Install"</strong>`,
    isLinkable: true,
    link: githubMarketplace,
    extraStyle: {marginLeft: 106, [LAPTOP]: {marginLeft: 20}},
    imageStyle: {[LAPTOP]: {marginLeft: -25}}
  },
  {
    icon: 'https://img.stackshare.io/fe/private/install-stackshare-app_2x.png',
    text: '<strong>Select the GitHub Org</strong> that you want to install StackShare on',
    extraStyle: {},
    imageStyle: {}
  },
  {
    icon: 'https://img.stackshare.io/fe/private/all_repo_2x.png',
    text:
      'Grant access to <strong>“All Repositories”</strong> or to <strong>“Only selected repos”</strong>. Any repos you select to grant access to in your GitHub Org will immediately have their own auto-generated private stack profiles on StackShare.',
    extraStyle: {marginLeft: 30, [LAPTOP]: {marginLeft: 20}},
    imageStyle: {[LAPTOP]: {marginLeft: -15}}
  },
  {
    icon: 'https://img.stackshare.io/fe/private/dashboard_graphic_2x.png',
    text:
      'Within the first 5 minutes after installing, you’ll see all your tech stacks and the people using them in one <strong>dashboard</strong>.',
    extraStyle: {},
    imageStyle: {}
  },
  {
    icon: 'https://img.stackshare.io/fe/private/report_graphic_2x.png',
    text:
      'Using the Tools Report, you’ll see all the <strong>versions</strong> of popular open source tools you’re using across your repos/stacks',
    extraStyle: {marginLeft: 40, [LAPTOP]: {marginLeft: 20}},
    imageStyle: {[LAPTOP]: {marginLeft: -25}}
  },
  {
    icon: 'https://img.stackshare.io/fe/private/alerts_graphic_2x.png',
    text:
      '<strong>Get alerts</strong> anytime a tool in your stacks is added, removed, or if a version changes. Get immediate, daily, or weekly alerts.',
    extraStyle: {},
    imageStyle: {width: 700}
  }
];

const Howitworks = () => {
  const isMobile = useContext(MobileContext);

  return (
    <Container>
      <SubContainer>
        <Title>How StackShare Enterprise Works</Title>
        <SubTitle>Install and get up and running in less than 5 minutes</SubTitle>
        <Content>
          {!isMobile && <LineStyled />}
          <Stepper>
            {!isMobile
              ? Steps.map((item, index) => {
                  if (index % 2 === 0)
                    return (
                      <Step key={index} placement={index}>
                        <StepText
                          dangerouslySetInnerHTML={{__html: sanitizeData(item.text)}}
                          extraStyle={item.extraStyle}
                        />
                        {item.isLinkable ? (
                          <StepLink href={item.link} target="_blank" title={item.text}>
                            <StepImage
                              src={item.icon}
                              imageStyle={item.imageStyle}
                              loading="lazy"
                            />
                          </StepLink>
                        ) : (
                          <StepImage src={item.icon} imageStyle={item.imageStyle} loading="lazy" />
                        )}
                      </Step>
                    );
                  else
                    return (
                      <Step key={index}>
                        {item.isLinkable ? (
                          <StepLink href={item.link} target="_blank" title={item.text}>
                            <StepImage
                              src={item.icon}
                              imageStyle={item.imageStyle}
                              loading="lazy"
                            />
                          </StepLink>
                        ) : (
                          <StepImage src={item.icon} imageStyle={item.imageStyle} loading="lazy" />
                        )}
                        <StepText dangerouslySetInnerHTML={{__html: sanitizeData(item.text)}} />
                      </Step>
                    );
                })
              : Steps.map((item, index) => {
                  return (
                    <Step key={index}>
                      {item.isLinkable ? (
                        <StepLink href={item.link} target="_blank" title={item.text}>
                          <StepImage src={item.icon} loading="lazy" />
                        </StepLink>
                      ) : (
                        <StepImage src={item.icon} loading="lazy" />
                      )}
                      <StepText dangerouslySetInnerHTML={{__html: sanitizeData(item.text)}} />
                    </Step>
                  );
                })}
          </Stepper>
        </Content>
        <Cta
          extraStyle={{
            width: '100%',
            marginTop: 0,
            marginBottom: 50
          }}
        />
      </SubContainer>
    </Container>
  );
};

export default Howitworks;
